import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../../Layout/DashboardLayout";
import ConfigTable from "../../../Common/Tables/ConfigTable";
import {
  Button,
  CancelButton,
  PageHeading,
  Icon,
  ConfirmDialog,
  employeeID,
  companyID,
} from "../../../Common/UIComponents";
import {
  fetchShifts,
  saveShifts,
  deleteTwoColumnListEntry,
} from "../../../API_Service/Settings_Slice";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const fmtTime = (raw) => {
  if (!raw) return "—";
  const parts = String(raw).split(":");
  if (parts.length < 2) return raw;
  const h = parseInt(parts[0], 10);
  const m = parts[1].padStart(2, "0");
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, "0")}:${m} ${h >= 12 ? "PM" : "AM"}`;
};

const calcDuration = (start, end) => {
  if (!start || !end) return null;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  const h = Math.floor(mins / 60),
    m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// Feeds Shift_Duration in Settings_Shift_CreateUpdate/Settings_LBB_ShiftCreateUpdate —
// sent as whole hours (6am-2pm -> 8), matching the int column.
export const calcDurationHrs = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.round(mins / 60);
};

const shiftMins = (start, end) => {
  if (!start || !end || start === end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let m = eh * 60 + em - (sh * 60 + sm);
  if (m < 0) m += 1440;
  return m;
};

const buildTableRows = (items = []) =>
  items
    .sort(
      (a, b) =>
        (a.Sort_Order ?? a.Shift_Num ?? 0) - (b.Sort_Order ?? b.Shift_Num ?? 0),
    )
    .map((item, i) => ({
      id: item.Shift_Timing_Id ?? `row-${i}`,
      shiftNo: item.Shift_Num ?? i + 1,
      shiftName: item.Shift_Name ?? `Shift ${item.Shift_Num ?? i + 1}`,
      startTime: (item.Shift_Start_Time ?? "").substring(0, 5),
      endTime: (item.Shift_End_Time ?? "").substring(0, 5),
      startDisplay: fmtTime(item.Shift_Start_Time),
      endDisplay: fmtTime(item.Shift_End_Time),
      duration: calcDuration(item.Shift_Start_Time, item.Shift_End_Time),
    }));

export const SHIFT_NAMES = [
  "First Shift/General Shift",
  "Second Shift",
  "Third Shift",
  "Fourth Shift",
  "Fifth Shift",
  "Sixth Shift",
  "Seventh Shift",
  "Eighth Shift",
];
export const getShiftName = (index) =>
  SHIFT_NAMES[index] ?? `Shift ${index + 1}`;

const toMins = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

const shiftsOverlap = (s1, e1, s2, e2) => {
  let a = toMins(s1),
    b = toMins(e1);
  let c = toMins(s2),
    d = toMins(e2);
  if (b <= a) b += 1440; // overnight
  if (d <= c) d += 1440;
  return a < d && c < b;
};

const EMPTY_SHIFT = { id: null, shiftName: "", startTime: "", endTime: "" };

const EXPORT_COLUMNS = [
  { key: "shiftName", title: "Shift Name" },
  { key: "startDisplay", title: "Start Time" },
  { key: "endDisplay", title: "End Time" },
  { key: "duration", title: "Duration" },
];

// ─────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────

const ShiftBadge = ({ shiftNo }) => (
  <div className="w-10 h-10 rounded-lg bg-[#e5e9e9] flex flex-col items-center justify-center text-[#08757c] leading-tight flex-shrink-0">
    <span className="text-[8px] font-black uppercase tracking-wider">SFT</span>
    <span className="text-sm font-extrabold leading-none">
      {String(shiftNo).padStart(2, "0")}
    </span>
  </div>
);

const FieldLabel = ({ children, required }) => (
  <label className="block text-xs font-bold text-[#3e494a] uppercase tracking-wider mb-2">
    {children}
    {required && <span className="text-[#ba1a1a] ml-1">*</span>}
  </label>
);

const TimeInput = ({ value, onChange, error }) => (
  <div>
    <input
      type="time"
      value={value}
      onChange={onChange}
      className={`w-full bg-white border border-[#ebeeee] rounded-lg px-4 py-2.5 text-[#181c1d]
                  text-sm font-medium focus:ring-2 focus:ring-[#08757c]/20 focus:border-[#08757c]/40
                  outline-none transition-all appearance-none ${error ? "ring-2 ring-[#ba1a1a]" : ""}`}
    />
    {error && <p className="text-xs text-[#ba1a1a] mt-1">{error}</p>}
  </div>
);

const Toast = ({
  message,
  color = "bg-[#bbf46e] text-[#112000]",
  icon = "check_circle",
}) => (
  <div
    className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] ${color} px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold`}
    role="status"
  >
    <Icon name={icon} className="text-[20px]" filled /> {message}
  </div>
);

// ─────────────────────────────────────────────
// SHIFT CARD
// ─────────────────────────────────────────────

const ShiftCard = ({
  shift,
  index,
  globalIndex,
  onChange,
  onRemove,
  canRemove,
  errors = {},
}) => {
  const nameIndex = shift._nameOverride
    ? Math.max(0, SHIFT_NAMES.indexOf(shift._nameOverride))
    : (globalIndex ?? index);
  const duration = calcDuration(shift.startTime, shift.endTime);

  return (
    <div className="relative bg-[#f7fafa] border border-[#ebeeee] rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShiftBadge shiftNo={nameIndex + 1} />
          <span className="text-sm font-bold text-[#181c1d]">
            {getShiftName(nameIndex)}
          </span>
          {duration && (
            <span className="px-2 py-0.5 bg-[#005b60]/10 text-[#005b60] text-xs font-bold rounded-full">
              {duration}
            </span>
          )}
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1.5 hover:bg-red-50 hover:text-red-600 text-[#3e494a] rounded-lg transition-colors"
            aria-label="Remove shift"
          >
            <span className="material-symbols-outlined text-[18px]">
              delete
            </span>
          </button>
        )}
      </div>

      <div>
        <FieldLabel required>Shift Name</FieldLabel>
        <input
          type="text"
          value={getShiftName(nameIndex)}
          readOnly
          disabled
          className="w-full bg-[#f1f4f4] border border-[#ebeeee] rounded-lg px-4 py-2.5
                     text-sm font-bold outline-none cursor-default text-[#181c1d]"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Start Time</FieldLabel>
          <TimeInput
            value={shift.startTime}
            onChange={(e) => onChange(index, "startTime", e.target.value)}
            error={errors.startTime}
          />
        </div>
        <div>
          <FieldLabel required>End Time</FieldLabel>
          <TimeInput
            value={shift.endTime}
            onChange={(e) => onChange(index, "endTime", e.target.value)}
            error={errors.endTime}
          />
        </div>
      </div>

      {shift.startTime && shift.endTime && (
        <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-[#ebeeee] text-xs text-[#3e494a]">
          <span className="material-symbols-outlined text-[#08757c] text-[14px]">
            schedule
          </span>
          <span className="font-semibold">{fmtTime(shift.startTime)}</span>
          <span className="text-[#aaa]">→</span>
          <span className="font-semibold">{fmtTime(shift.endTime)}</span>
          {duration && (
            <span className="ml-auto font-bold text-[#08757c]">{duration}</span>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// SHIFT DRAWER
// ─────────────────────────────────────────────

export const ShiftDrawer = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
  saveError,
  editRow,
  existingCount = 0,
  lastEndTime = "",
  existingRows = [],
}) => {
  const [shifts, setShifts] = useState([{ ...EMPTY_SHIFT }]);
  const [errors, setErrors] = useState([{}]);
  const [sessionSaved, setSessionSaved] = useState(0);
  const prevExistingCount = useRef(existingCount);

  const initShifts = useCallback((count, endTime) => {
    const remaining = Math.max(1, 3 - count);
    setShifts(
      Array.from({ length: remaining }, (_, i) => ({
        ...EMPTY_SHIFT,
        shiftName: getShiftName(count + i),
        startTime: i === 0 ? endTime || "" : "",
      })),
    );
    setErrors(Array(remaining).fill({}));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (editRow) {
      setShifts([
        {
          id: editRow.id,
          shiftName: editRow.shiftName ?? "",
          startTime: editRow.startTime ?? "",
          endTime: editRow.endTime ?? "",
          _nameOverride: editRow.shiftName,
          _shiftNo: editRow.shiftNo,
        },
      ]);
      setErrors([{}]);
    } else {
      initShifts(existingCount, lastEndTime);
    }
  }, [editRow, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) setSessionSaved(0);
  }, [isOpen]);

  useEffect(() => {
    if (existingCount > prevExistingCount.current && isOpen && !editRow) {
      setSessionSaved(0);
      initShifts(existingCount, lastEndTime);
    }
    prevExistingCount.current = existingCount;
  }, [existingCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((index, field, value) => {
    setShifts((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
    setErrors((prev) =>
      prev.map((e, i) => (i === index ? { ...e, [field]: "" } : e)),
    );
  }, []);

  const handleAddShift = () => {
    setShifts((prev) => {
      const nextStart = prev[prev.length - 1]?.endTime || "";
      const nextIndex = existingCount + sessionSaved + prev.length;
      return [
        ...prev,
        {
          ...EMPTY_SHIFT,
          shiftName: getShiftName(nextIndex),
          startTime: nextStart,
        },
      ];
    });
    setErrors((prev) => [...prev, {}]);
  };

  const handleRemove = (index) => {
    setShifts((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => prev.filter((_, i) => i !== index));
  };

  // Returns which field of the new shift falls inside the conflicting shift.
  // Returns { onStart, onEnd } — true when that boundary is the issue.
  const overlapField = (s1, e1, s2, e2) => {
    let a = toMins(s1),
      b = toMins(e1);
    let c = toMins(s2),
      d = toMins(e2);
    if (b <= a) b += 1440;
    if (d <= c) d += 1440;
    // Strictly-inside checks
    const startInside = a > c && a < d;
    const endInside = b > c && b < d;
    // If new shift contains the existing one entirely, flag both
    const newContains = a <= c && b >= d;
    return {
      onStart: startInside || newContains,
      onEnd: endInside || newContains,
    };
  };

  const fmtRange = (s, e) => `${fmtTime(s)} – ${fmtTime(e)}`;

  const validate = () => {
    let valid = true;
    const newErrors = shifts.map((s, i) => {
      const e = {};
      if (!s.startTime) {
        e.startTime = "Please pick a start time.";
        valid = false;
      }
      if (!s.endTime) {
        e.endTime = "Please pick an end time.";
        valid = false;
      }

      if (s.startTime && s.endTime) {
        if (s.startTime === s.endTime) {
          e.endTime =
            "End time can't be the same as start time — that would make this a 24-hour shift.";
          valid = false;
        }

        // Check overlap against already saved shifts (exclude the one being edited)
        const savedConflict = existingRows.find(
          (r) =>
            r.id !== editRow?.id &&
            r.startTime &&
            r.endTime &&
            shiftsOverlap(s.startTime, s.endTime, r.startTime, r.endTime),
        );
        // Check overlap within the shifts being added in this session (check later shifts first)
        let sessionConflict = null;
        for (let j = shifts.length - 1; j >= 0; j--) {
          const other = shifts[j];
          if (
            j !== i &&
            other.startTime &&
            other.endTime &&
            shiftsOverlap(
              s.startTime,
              s.endTime,
              other.startTime,
              other.endTime,
            )
          ) {
            sessionConflict = other;
            break;
          }
        }
        const conflict = savedConflict || sessionConflict;
        if (conflict) {
          const conflictName = conflict.shiftName || "another shift";
          const conflictRange = fmtRange(conflict.startTime, conflict.endTime);
          const { onStart, onEnd } = overlapField(
            s.startTime,
            s.endTime,
            conflict.startTime,
            conflict.endTime,
          );

          if (onEnd && !onStart) {
            e.endTime = `End time falls inside ${conflictName} (${conflictRange}). Please pick an earlier time.`;
          } else if (onStart && !onEnd) {
            e.startTime = `Start time falls inside ${conflictName} (${conflictRange}). Please pick a later time.`;
          } else {
            // Both sides clash, or new shift completely covers the other
            e.endTime = `This timing clashes with ${conflictName} (${conflictRange}). Please choose a non-overlapping range.`;
          }
          valid = false;
        }
      }

      return e;
    });

    // Total coverage must not exceed 24 hours across all shifts
    const existingMins = existingRows
      .filter((r) => r.id !== editRow?.id && r.startTime && r.endTime)
      .reduce((sum, r) => sum + shiftMins(r.startTime, r.endTime), 0);

    const newMins = shifts.reduce(
      (sum, s) => sum + shiftMins(s.startTime, s.endTime),
      0,
    );

    if (existingMins + newMins > 1440) {
      const remaining = 1440 - existingMins;
      const rh = Math.floor(remaining / 60);
      const rm = remaining % 60;
      const remainingStr = rm > 0 ? `${rh}h ${rm}m` : `${rh}h`;
      shifts.forEach((s, i) => {
        if (s.startTime && s.endTime && !newErrors[i].endTime) {
          newErrors[i].endTime =
            `All shifts together would cover more than 24 hours. You have only ${remainingStr} left in the day — please shorten this one.`;
          valid = false;
        }
      });
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave(shifts, editRow?.id ?? null, existingCount);
  };

  const totalDuration = useMemo(() => {
    let total = 0;
    shifts.forEach((s) => {
      if (!s.startTime || !s.endTime) return;
      const [sh, sm] = s.startTime.split(":").map(Number);
      const [eh, em] = s.endTime.split(":").map(Number);
      let m = eh * 60 + em - (sh * 60 + sm);
      if (m < 0) m += 24 * 60;
      total += m;
    });
    if (!total) return null;
    const h = Math.floor(total / 60),
      m = total % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }, [shifts]);

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-all duration-300
          ${isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`}
        onClick={onClose}
      />
      <aside
        className={`fixed right-0 top-0 z-[60] h-screen w-full max-w-md bg-white shadow-2xl
                    flex flex-col transition-transform duration-300 ease-in-out
                    ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="p-4 border-b border-[#f1f4f4] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-[#08757c]">
              schedule
            </span>
            <h3 className="text-base font-bold text-[#181c1d]">
              {editRow ? "Edit Shift" : "Configure Shifts"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#f1f4f4] rounded-full transition-colors"
          >
            <span className="material-symbols-outlined text-[#3e494a]">
              close
            </span>
          </button>
        </div>

        <div className="flex-1 p-4 space-y-3 overflow-y-auto">
          {saveError && (
            <div className="bg-red-50 text-[#ba1a1a] rounded-xl px-4 py-3 text-sm flex items-center gap-3">
              <span className="material-symbols-outlined text-[20px]">
                error
              </span>
              {saveError}
            </div>
          )}

          {shifts.length > 1 && totalDuration && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#005b60]/8 rounded-lg border border-[#005b60]/15">
              <span className="material-symbols-outlined text-[#08757c] text-[16px]">
                summarize
              </span>
              <span className="text-xs text-[#3e494a] font-medium">
                <span className="font-bold text-[#005b60]">
                  {shifts.length} shifts
                </span>
                {" · "}
                <span className="font-bold text-[#005b60]">
                  {totalDuration}
                </span>{" "}
                total coverage
              </span>
            </div>
          )}

          {shifts.map((shift, i) => (
            <ShiftCard
              key={i}
              shift={shift}
              index={i}
              globalIndex={existingCount + sessionSaved + i}
              onChange={handleChange}
              onRemove={handleRemove}
              canRemove={shifts.length > 1 && !editRow}
              errors={errors[i] ?? {}}
            />
          ))}

          {!editRow && existingCount + sessionSaved + shifts.length < 3 && (
            <button
              type="button"
              onClick={handleAddShift}
              className="w-full py-3 border-2 border-dashed border-[#bdc9c9] rounded-xl text-sm font-semibold
                         text-[#3e494a] hover:border-[#08757c] hover:text-[#08757c] hover:bg-[#f7fafa]
                         transition-all flex items-center justify-center gap-2"
            >
              <span className="material-symbols-outlined text-[18px]">
                add_circle
              </span>
              Add Another Shift
            </button>
          )}

          <p className="text-xs text-[#3e494a] italic bg-[#f1f4f4]/50 p-4 rounded-lg leading-relaxed">
            <span className="text-[#ba1a1a] font-bold">*</span> All fields are
            mandatory per shift. Overnight shifts are supported and duration is
            calculated automatically.
          </p>
        </div>

        <div className="p-4 border-t border-[#f1f4f4] flex gap-3 flex-shrink-0">
          <CancelButton onClick={onClose} />
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex-[2]"
          >
            {isSaving ? (
              <>
                <span className="material-symbols-outlined text-[18px] animate-spin">
                  progress_activity
                </span>
                Saving…
              </>
            ) : editRow ? (
              "Update Shift"
            ) : (
              `Save ${shifts.length > 1 ? `${shifts.length} Shifts` : "Shift"}`
            )}
          </Button>
        </div>
      </aside>
    </>
  );
};

// ─────────────────────────────────────────────
// STATS ROW
// ─────────────────────────────────────────────

const StatsRow = ({ rows }) => {
  const total = rows.length;
  const { totalCoverage, has24h } = useMemo(() => {
    let mins = 0;
    rows.forEach((r) => {
      if (!r.startTime || !r.endTime) return;
      const [sh, sm] = r.startTime.split(":").map(Number);
      const [eh, em] = r.endTime.split(":").map(Number);
      let m = eh * 60 + em - (sh * 60 + sm);
      if (m < 0) m += 24 * 60;
      mins += m;
    });
    const h = Math.floor(mins / 60),
      m = mins % 60;
    return {
      totalCoverage: mins ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : "—",
      has24h: mins >= 1440,
    };
  }, [rows]);

  const stats = [
    {
      label: "Total Shifts",
      value: total || "—",
      icon: "view_timeline",
      bg: "bg-[#005b60]/10",
      color: "text-[#005b60]",
    },
    {
      label: "Total Coverage",
      value: total ? totalCoverage : "—",
      icon: "schedule",
      bg: "bg-[#8fc444]/10",
      color: "text-[#436900]",
    },
    {
      label: "24h Coverage",
      value: total ? (has24h ? "Yes" : "No") : "—",
      icon: "brightness_4",
      bg: "bg-[#0088cc]/10",
      color: "text-[#0088cc]",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
      {stats.map((s) => (
        <div
          key={s.label}
          className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3"
        >
          <div
            className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center ${s.color} flex-shrink-0`}
          >
            <span className="material-symbols-outlined text-[22px]">
              {s.icon}
            </span>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">
              {s.label}
            </p>
            <p className="text-xl font-bold text-slate-900 leading-tight">
              {s.value}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function ShiftConfigIndex({ embedded = false } = {}) {
  const dispatch = useDispatch();
  const location = useLocation();

  const [shifts, setShifts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [orderError, setOrderError] = useState(null);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const fetchList = useCallback(() => {
    setIsLoading(true);
    setSettingsError(null);
    dispatch(fetchShifts({ ID: 0, companyID }))
      .then((res) => setShifts(toArray(res)))
      .catch((err) => setSettingsError(err?.message ?? "Failed to load shifts"))
      .finally(() => setIsLoading(false));
  }, [dispatch]);

  useEffect(() => {
    fetchList();
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAddDrawer = useCallback(() => {
    setEditRow(null);
    setSaveError(null);
    setSaveSuccess(false);
    setDrawerOpen(true);
  }, []);
  const openEditDrawer = useCallback((row) => {
    setEditRow(row);
    setSaveError(null);
    setSaveSuccess(false);
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setEditRow(null);
    setSaveError(null);
    setSaveSuccess(false);
  }, []);

  const rows = useMemo(() => buildTableRows(shifts), [shifts]);
  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.shiftName.toLowerCase().includes(q) ||
        r.startDisplay.toLowerCase().includes(q) ||
        r.endDisplay.toLowerCase().includes(q),
    );
  }, [searchQuery, rows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(
    () =>
      filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredRows, currentPage, pageSize],
  );

  const columns = useMemo(
    () => [
      {
        key: "shiftName",
        title: "Shift",
        render: (row) => (
          <div className="flex items-center gap-3">
            <ShiftBadge shiftNo={row.shiftNo} />
            <span className="font-semibold text-[#181c1d] text-sm">
              {row.shiftName}
            </span>
          </div>
        ),
      },
      {
        key: "startTime",
        title: "Start Time",
        render: (row) => (
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[#08757c] text-[14px]">
              login
            </span>
            <span className="font-medium text-[#181c1d] text-sm">
              {row.startDisplay}
            </span>
          </div>
        ),
      },
      {
        key: "endTime",
        title: "End Time",
        render: (row) => (
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[#08757c] text-[14px]">
              logout
            </span>
            <span className="font-medium text-[#181c1d] text-sm">
              {row.endDisplay}
            </span>
          </div>
        ),
      },
      {
        key: "duration",
        title: "Duration",
        render: (row) =>
          row.duration ? (
            <span className="px-2 py-1 bg-[#e5e9e9] text-[#3e494a] text-xs font-bold rounded-lg">
              {row.duration}
            </span>
          ) : (
            <span className="text-xs text-[#3e494a]/40 italic">—</span>
          ),
      },
    ],
    [],
  );

  const handleSave = useCallback(
    (shiftForms, id, existingShiftCount) => {
      setIsSaving(true);
      setSaveError(null);

      const isEdit = !!id;
      const payload = {
        CompanyID: companyID,
        CreatedBy: employeeID,
        Shifts: shiftForms.map((s, i) => ({
          ShiftTimingId: isEdit ? id : (s.id ?? 0),
          ShiftNo: isEdit
            ? (s._shiftNo ?? existingShiftCount + i + 1)
            : existingShiftCount + i + 1,
          ShiftName: getShiftName(
            isEdit
              ? Math.max(0, SHIFT_NAMES.indexOf(s.shiftName))
              : existingShiftCount + i,
          ),
          ShiftStartTime: s.startTime ? `${s.startTime.slice(0, 5)}:00` : null,
          ShiftEndTime: s.endTime ? `${s.endTime.slice(0, 5)}:00` : null,
          ShiftDuration: calcDurationHrs(s.startTime, s.endTime),
        })),
      };

      dispatch(saveShifts(payload))
        .then((res) => {
          const result = res?.payload?.data ?? res?.payload;
          if (Number(result) > 0) {
            fetchList();
            closeDrawer();
            showToast(
              isEdit
                ? "Shift updated successfully."
                : "Shift saved successfully.",
            );
          } else {
            setSaveError("Failed to save. Please try again.");
          }
        })
        .catch((err) => setSaveError(err?.message ?? "Failed to save shift"))
        .finally(() => setIsSaving(false));
    },
    [dispatch, fetchList, closeDrawer],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);
  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleDelete = useCallback(
    (row) => {
      const maxShiftNo = Math.max(...rows.map((r) => r.shiftNo));
      if (row.shiftNo !== maxShiftNo) {
        const lastShift = rows.find((r) => r.shiftNo === maxShiftNo);
        setOrderError(
          `To maintain shift order, please remove "${lastShift?.shiftName}" before deleting "${row.shiftName}".`,
        );
        setTimeout(() => setOrderError(null), 4000);
        return;
      }
      setDeleteDialog({ open: true, row });
    },
    [rows],
  );
  const handleCancelDelete = useCallback(() => {
    setDeleteDialog({ open: false, row: null });
    setDeleteBlocked(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    const { row } = deleteDialog;
    if (!row) return;
    setDeleteLoading(true);
    setDeleteError(null);
    dispatch(
      deleteTwoColumnListEntry({
        term: "SHIFT",
        id: row.id,
        modifiedBy: employeeID,
        companyID,
      }),
    )
      .then((action) => {
        const result = action?.payload?.data;
        if (Number(result) === -1) {
          setDeleteBlocked(true);
          return;
        }
        setDeleteBlocked(false);
        setDeleteDialog({ open: false, row: null });
        fetchList();
        showToast("Shift deleted successfully.");
      })
      .catch((err) => setDeleteError(err?.message ?? "Failed to delete"))
      .finally(() => setDeleteLoading(false));
  }, [deleteDialog, dispatch, fetchList]); // eslint-disable-line react-hooks/exhaustive-deps

  const inner = (
    <>
      <div className="px-8 py-4 bg-[#f7fafa] text-[#181c1d] min-h-screen">
        <div className="flex flex-wrap justify-between items-end mb-3 gap-4">
          <PageHeading
            title="Shift Configuration"
            subtitle="Define and manage company work shifts. Configure start and end times for each shift — all shifts are saved together in a single operation."
          />
          {rows.length < 3 && (
            <Button variant="primary" onClick={openAddDrawer}>
              <Icon name="schedule" className="mr-1 text-[20px]" /> Add Shift
            </Button>
          )}
        </div>

        <StatsRow rows={rows} />

        <ConfigTable
          columns={columns}
          rows={isLoading ? [] : paginatedRows}
          onEdit={openEditDrawer}
          onDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          // onPageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          allFilteredRows={filteredRows}
          exportColumns={EXPORT_COLUMNS}
          exportFilename="shifts"
          emptyText={isLoading ? "Loading…" : "No shifts configured yet."}
          showPagination={false}
        />

        {settingsError && (
          <div className="mt-6 rounded-xl bg-red-50 text-red-700 p-6 text-sm">
            {settingsError}
          </div>
        )}

        <section className="mt-5 flex gap-3 p-4 bg-[#f1f4f4] rounded-xl items-start border border-[#e5e9e9]">
          <span
            className="material-symbols-outlined text-[#8fc444] text-2xl flex-shrink-0"
            style={{ fontVariationSettings: "'FILL' 1" }}
          >
            info
          </span>
          <div className="space-y-1">
            <p className="font-bold text-sm text-[#181c1d]">
              Shift-Aware TAT Calculation
            </p>
            <p className="text-sm text-[#3e494a] leading-relaxed">
              Once shifts are saved, the Lab Planning engine will use these
              windows to calculate realistic turnaround times. Samples received
              outside active shift hours will be queued to the next available
              shift automatically.
            </p>
          </div>
        </section>
      </div>

      <ShiftDrawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        onSave={handleSave}
        isSaving={isSaving}
        saveError={saveError}
        editRow={editRow}
        existingCount={rows.length}
        lastEndTime={rows.length > 0 ? rows[rows.length - 1].endTime : ""}
        existingRows={rows}
      />

      <ConfirmDialog
        isOpen={deleteDialog.open}
        variant="danger"
        isLoading={deleteLoading}
        errorMessage={
          deleteBlocked
            ? "This shift is already mapped to an associate. Please remove the mapping before deleting."
            : null
        }
        cancelLabel={deleteBlocked ? "OK, Got it" : "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {toastMsg && <Toast message={toastMsg} />}
      {deleteError && (
        <Toast message={deleteError} color="bg-red-600 text-white" />
      )}
      {orderError && (
        <Toast
          message={orderError}
          color="bg-amber-100 text-amber-900 border border-amber-400"
          icon="warning"
        />
      )}
    </>
  );
  return embedded ? inner : <DashboardLayout>{inner}</DashboardLayout>;
}
